import React, { useMemo, HTMLAttributes, CSSProperties } from 'react';
import { css } from '@emotion/react';
import { PaginationItem } from './PaginationItem';
import { PaginationArrow } from './PaginationArrow';
import { pagination_base_css } from './styles';
import { useProviderConfig } from '../provider';

export interface PaginationProps extends Omit<HTMLAttributes<HTMLUListElement>, 'onChange'> {
  disabled?: boolean;
  /**
   * 전체 페이지 갯수, 음수일 경우 렌더되지 않습니다.
   */
  totalPages: number;
  /** 현재 페이지 번호 */
  page: number;
  /**
   * 현재페이지 기준 좌/우 표시할 페이지 버튼 갯수
   * @default 4
   */
  siblingCount?: number;
  /**
   * 맨처음/맨끝 표시할 페이지 버튼 갯수
   * @default 1
   */
  boundaryCount?: number;
  /**
   * Pagination 좌/우 화살표 인디케이터 사용 유무
   * @default true
   */
  hasArrows?: boolean;
  /** 페이지가 변경될 때 호출되는 함수 */
  onChange?: (page: number) => void;
  /**
   * Pagination 정렬
   */
  align?: CSSProperties['justifyContent'];
}

const ELLIPSIS = 'ellipsis';

const getPaginationRange = (start: number, end: number) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, i) => start + i);
};

export const Pagination = ({
  disabled,
  totalPages,
  page,
  siblingCount,
  boundaryCount = 1,
  hasArrows,
  onChange,
  align,
}: PaginationProps) => {
  const { components: { pagination: contextPagination } = { pagination: { siblingCount: 4, hasArrows: true } } } =
    useProviderConfig();
  const mergedSiblingCount = useMemo(
    () => siblingCount ?? contextPagination?.siblingCount,
    [siblingCount, contextPagination?.siblingCount],
  );
  const mergedHasArrows = useMemo(
    () => hasArrows ?? contextPagination?.hasArrows ?? contextPagination?.showArrowButton,
    [hasArrows, contextPagination?.hasArrows, contextPagination?.showArrowButton],
  );

  const firstPage = 1;
  const lastPage = totalPages;

  const paginationItems: (string | number)[] = useMemo(() => {
    // boundaryCount에 따른 시작/종료 범위
    const startRange = getPaginationRange(1, Math.min(boundaryCount, totalPages));
    const endRange = getPaginationRange(Math.max(totalPages - boundaryCount + 1, boundaryCount + 1), totalPages);

    // 현재페이지 기준 좌/우 표시할 페이지 인덱스
    const siblingStartIndex = Math.max(
      Math.min(page - (mergedSiblingCount ?? 0), totalPages - boundaryCount - (mergedSiblingCount ?? 0) * 2 - 1),
      boundaryCount + 2,
    );
    const siblingEndIndex = Math.min(
      Math.max(page + (mergedSiblingCount ?? 0), boundaryCount + (mergedSiblingCount ?? 0) * 2 + 2),
      endRange.length > 0 ? endRange[0] - 2 : totalPages - 1,
    );

    // middle range 범위 여부
    const isLeftPageOutOfRange = siblingStartIndex > boundaryCount + 2;
    const isRightPageOutOfRange = siblingEndIndex < totalPages - boundaryCount - 1;

    const leftRange = isLeftPageOutOfRange
      ? [ELLIPSIS]
      : boundaryCount + 1 < totalPages - boundaryCount
      ? [boundaryCount + 1]
      : [];
    const rightRange = isRightPageOutOfRange
      ? [ELLIPSIS]
      : totalPages - boundaryCount > boundaryCount
      ? [totalPages - boundaryCount]
      : [];

    // page, totalPages 변경에 따른 middle range 범위
    const middleRange = [...leftRange, ...getPaginationRange(siblingStartIndex, siblingEndIndex), ...rightRange];

    return [...startRange, ...middleRange, ...endRange];
  }, [totalPages, page, mergedSiblingCount, boundaryCount]);

  const renderToPaginationItems = () => {
    return paginationItems.map((itemPage, index) => {
      if (itemPage === ELLIPSIS) {
        return <PaginationItem key={index} selected={false} disabled={disabled} isNumberOutOfRange />;
      }
      return (
        <PaginationItem
          key={index}
          label={itemPage}
          disabled={disabled}
          selected={page === itemPage}
          onClick={() => onChange?.(itemPage as number)}
        />
      );
    });
  };

  return (
    <ul
      css={[
        pagination_base_css,
        css`
          ${align && `justify-content: ${align}`}
        `,
      ]}
    >
      {mergedHasArrows && paginationItems.length > 0 ? (
        <>
          <li>
            <PaginationArrow
              direction='prev'
              disabled={disabled || page === firstPage}
              onClick={() => onChange?.(page - 1)}
            />
          </li>
          {renderToPaginationItems()}
          <li>
            <PaginationArrow
              direction='next'
              disabled={disabled || page === lastPage}
              onClick={() => onChange?.(page + 1)}
            />
          </li>
        </>
      ) : (
        renderToPaginationItems()
      )}
    </ul>
  );
};
